import { UseMutationResult, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { getOrderSearch, getWorkInvoices, getWorkItems, saveWorkRegistrations } from './apis'
import {
  OrderSearchQuery,
  OrderSearchRow,
  SaveReq,
  SaveRes,
  toOrderSearchRow,
  toWorkInvoiceHead,
  toWorkInvoiceMeta,
  toWorkTableRow,
  WorkInvoiceHead,
  WorkInvoiceMeta,
  WorkInvoiceQuery,
  WorkItemQuery,
  WorkTableRow,
} from './types'

const PATH = ['return', 'rgr11'] as const

export const rgr11QueryKeys = {
  all: PATH,
  invoices: (query: WorkInvoiceQuery | null) => [...PATH, 'INVOICES', query] as const,
  items: (query: WorkItemQuery | null) => [...PATH, 'ITEMS', query] as const,
  orderSearch: (query: OrderSearchQuery | null) => [...PATH, 'ORDER_SEARCH', query] as const,
}

const EMPTY_META: WorkInvoiceMeta = { ordNoMax: null, giIds: [], packageType: null }

/** HEAD 조건이 안 갖춰지면 호출하지 않고 빈 결과를 낸다(3.1 "미지정이면 빈 배열" 가드를 조립 단계에서 막는다). */
const hasInvoiceQuery = (query: WorkInvoiceQuery | null): query is WorkInvoiceQuery =>
  !!query && !!query.shipper && !!query.actDateFrom && !!query.actDateTo && (!!query.rtnInvoiceNo || !!query.orgInvoiceNo)

interface WorkInvoicesQueryData {
  head: WorkInvoiceHead | null
  meta: WorkInvoiceMeta
}

export type UseGetWorkInvoicesResult = WorkInvoicesQueryData & Omit<UseQueryResult<WorkInvoicesQueryData>, 'data'>

/**
 * 반송장·원송장·합포 불러오기 조회(3.1).
 *
 * 진입 즉시 조회하지 않는다(`enabled: false`) — 반송장/원송장 Enter·합포 불러오기 버튼으로만 실행한다.
 * 항상 재조회한다(1.0 동작과 같다 — `useReturnReceiptQueries.ts` 선례와 같은 이유).
 */
export const useGetWorkInvoices = (query: WorkInvoiceQuery | null): UseGetWorkInvoicesResult => {
  const { data, ...rest } = useQuery({
    queryKey: rgr11QueryKeys.invoices(query),
    queryFn: async (): Promise<WorkInvoicesQueryData> => {
      if (!hasInvoiceQuery(query)) return { head: null, meta: EMPTY_META }
      const res = await getWorkInvoices(query)
      const rows = res.data?.items ?? []
      return { head: rows[0] ? toWorkInvoiceHead(rows[0]) : null, meta: toWorkInvoiceMeta(rows) }
    },
    enabled: false,
    // 실패 안내는 스캔 화면이 큰 토스트로 한다(scanNotice) — 전역 QueryCache 알림(새 알럿)까지 뜨면 확인 버튼이 다음 스캔을 막는다(#899 리뷰).
    meta: { noErrorMessage: true },
  })

  return { head: data?.head ?? null, meta: data?.meta ?? EMPTY_META, ...rest }
}

export type UseGetWorkItemsResult = { items: WorkTableRow[] } & Omit<UseQueryResult<WorkTableRow[]>, 'data'>

/**
 * 상품바코드·마스터 조회여부 조회(3.2).
 *
 * `barcode` 없으면 호출하지 않는다(3.2 "없으면 빈 배열"). 반송장/원송장/`giId` 조합 가드는
 * 상위(스캔 워크플로 훅)가 호출 시점을 결정하는 방식으로 지킨다 — 분기가 복잡해 훅 하나에
 * 다시 넣으면 서버 규칙을 한 번 더(다르게) 베끼게 된다.
 */
export const useGetWorkItems = (query: WorkItemQuery | null): UseGetWorkItemsResult => {
  const { data, ...rest } = useQuery({
    queryKey: rgr11QueryKeys.items(query),
    queryFn: async (): Promise<WorkTableRow[]> => {
      if (!query?.barcode) return []
      const res = await getWorkItems(query)
      const rows = res.data?.items ?? []
      return rows.map((row, i) => toWorkTableRow(row, '', `scan-${Date.now()}-${i}`))
    },
    enabled: false,
    // 실패 안내는 스캔 화면이 큰 토스트로 한다(scanNotice) — 전역 QueryCache 알림(새 알럿)까지 뜨면 확인 버튼이 다음 스캔을 막는다(#899 리뷰).
    meta: { noErrorMessage: true },
  })

  return { items: data ?? [], ...rest }
}

export type UseGetOrderSearchResult = { items: OrderSearchRow[] } & Omit<UseQueryResult<OrderSearchRow[]>, 'data'>

/** 주문검색 팝업 조회(3.4). `actDateFrom`·`actDateTo` 없으면 호출하지 않는다. */
export const useGetOrderSearch = (query: OrderSearchQuery | null): UseGetOrderSearchResult => {
  const { data, ...rest } = useQuery({
    queryKey: rgr11QueryKeys.orderSearch(query),
    queryFn: async (): Promise<OrderSearchRow[]> => {
      if (!query?.actDateFrom || !query?.actDateTo) return []
      const res = await getOrderSearch(query)
      const rows = res.data?.items ?? []
      return rows.map((row, i) => toOrderSearchRow(row, `order-${i}`))
    },
    enabled: false,
    // 실패 안내는 화면이 한다(refetch throwOnError → 서버 문구, 없으면 화면 문구) — 전역 QueryCache 알럿과 겹쳐 두 번 뜨지 않게 끈다.
    meta: { noErrorMessage: true },
  })

  return { items: data ?? [], ...rest }
}

export const RGR11_SAVE_MUTATION_KEY = ['return', 'rgr11', 'SAVE'] as const

/**
 * 저장·검수완료(3.3). `data` 가 비어 오면(예상 밖 응답) 성공으로 위장하지 않고 던진다 — 호출부가
 * 저장 실패로 처리해 표를 비우지 않게 한다(PR #691 리뷰 코멘트).
 */
export const useSaveWorkRegistrations = (): UseMutationResult<SaveRes, Error, SaveReq> =>
  useMutation({
    mutationKey: RGR11_SAVE_MUTATION_KEY,
    mutationFn: async (body: SaveReq): Promise<SaveRes> => {
      const res = await saveWorkRegistrations(body)
      if (!res.data) throw new Error('저장 응답이 비어 있습니다.')
      return res.data
    },
  })
